import { useCallback, useEffect, useState } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import type { DismissActionType } from './types'
import { useCommentsCounts } from '../hooks/comments/useCommentsCounts'
import type { CommentsCountFilters } from '../hooks/comments/useCommentsCounts'
import { useDismissAll } from '../hooks/comments/useDismissAll'
import { getOneMonthAgoIso } from '../util/dateHelpers'
import { useStableArray } from '../util/useStableArray'

export const RESOLVE_COMMENTS_LIMIT = 1_000

type UseResolveCommentsDialogParams = {
  channelIds: string[]
  postId?: string
  actionType: DismissActionType
  shouldOpen: boolean
  onOpenChange?: (open: boolean) => void
  onHideTooltip?: () => void
  onResolvingChange?: (isResolving: boolean) => void
}

export const useResolveCommentsDialog = ({
  channelIds,
  postId,
  actionType,
  shouldOpen,
  onOpenChange,
  onHideTooltip,
  onResolvingChange,
}: UseResolveCommentsDialogParams): {
  isOpen: boolean
  handleOpenChange: (open: boolean) => void
  handleResolveComments: () => Promise<void>
  isResolving: boolean
  commentCount: number | null
  isLoadingCount: boolean
  errorCount: Error | null
  refetch: () => void
  effectiveLimit: number | null
} => {
  // Dialog state
  const [isResolving, setIsResolving] = useState(false)

  // Comment count state (only for 'older-than-month')
  const [commentCount, setCommentCount] = useState<number | null>(null)
  const [isLoadingCount, setIsLoadingCount] = useState(false)
  const [errorCount, setErrorCount] = useState<Error | null>(null)
  const [effectiveLimit, setEffectiveLimit] = useState<number | null>(null)

  const organizationId = useOrganizationId()
  const { dismissAllForChannels, dismissAllForPost } = useDismissAll()
  const { fetchCounts } = useCommentsCounts()

  // Stabilize channelIds array reference to prevent infinite loops
  const stableChannelIds = useStableArray(channelIds)

  // Fetch comment count for both action types
  const fetchCommentCount = useCallback(async (): Promise<void> => {
    if (!organizationId) return
    if (actionType === 'post' && !postId) return

    setIsLoadingCount(true)
    setErrorCount(null)
    setEffectiveLimit(null)

    try {
      const filters: CommentsCountFilters = {
        channelIds: stableChannelIds,
        status: ['unreplied'],
        ...(actionType === 'post' && postId && { postId: [postId] }),
        ...(actionType === 'older-than-month' && {
          publishedAt: {
            end: getOneMonthAgoIso(),
          },
        }),
      }

      const result = await fetchCounts({
        organizationId,
        filters,
        limit: RESOLVE_COMMENTS_LIMIT,
      })

      const counts = result.data?.commentsCounts?.counts ?? []
      const count = counts.reduce((sum, item) => sum + (item.count ?? 0), 0)

      // Get the effective limit returned by the backend
      const backendLimit = counts[0]?.limit ?? null
      setEffectiveLimit(backendLimit)
      setCommentCount(count)
    } catch (error) {
      setErrorCount(error instanceof Error ? error : new Error('Unknown error'))
    } finally {
      setIsLoadingCount(false)
    }
  }, [fetchCounts, organizationId, stableChannelIds, actionType, postId])

  // Handle dialog open/close state
  useEffect(() => {
    if (shouldOpen) {
      // Fetch count when dialog opens for both action types
      void fetchCommentCount()
    } else {
      // Reset state when dialog closes
      setCommentCount(null)
      setErrorCount(null)
      setIsLoadingCount(false)
      setEffectiveLimit(null)
    }
  }, [shouldOpen, actionType, fetchCommentCount])

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      onOpenChange?.(open)

      if (!open) {
        onHideTooltip?.()
        // Reset state when closing
        setCommentCount(null)
        setErrorCount(null)
        setIsLoadingCount(false)
        setEffectiveLimit(null)
      }
    },
    [onOpenChange, onHideTooltip],
  )

  const handleResolveComments = useCallback(async (): Promise<void> => {
    setIsResolving(true)
    onResolvingChange?.(true)
    try {
      const toDate =
        actionType === 'older-than-month' ? getOneMonthAgoIso() : undefined

      if (actionType === 'post') {
        if (!postId) {
          throw new Error('A post id is required')
        }
        await dismissAllForPost(postId)
      } else {
        await dismissAllForChannels(stableChannelIds, toDate)
      }
      onOpenChange?.(false)
      onHideTooltip?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error(message)
    } finally {
      setIsResolving(false)
      onResolvingChange?.(false)
    }
  }, [
    dismissAllForChannels,
    dismissAllForPost,
    stableChannelIds,
    postId,
    actionType,
    onOpenChange,
    onHideTooltip,
    onResolvingChange,
  ])

  // Retry function for failed count fetches
  const refetch = useCallback((): void => {
    if (actionType === 'older-than-month' || actionType === 'post') {
      void fetchCommentCount()
    }
  }, [fetchCommentCount, actionType])

  return {
    // Dialog state
    isOpen: shouldOpen,
    handleOpenChange,
    handleResolveComments,
    isResolving,

    // Comment count state
    commentCount,
    isLoadingCount,
    errorCount,
    refetch,
    effectiveLimit,
  }
}
